import React from 'react';

import { Skeleton } from '../../../../components';
import { StatValue } from '../StatValue';
import { Delta } from '../Delta';
import { Root, Label, Value, Cap } from './Stat.styles';

export type StatProps = {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  /** Stagger the count-up start. */
  delay?: number;
  /** Inline delta pill after the value (omit for a bare figure). */
  delta?: number;
  /** Caption under the value ("vs last week"). */
  caption?: string;
  /** Tighten the value's top margin to 6px (bare split stats). */
  compact?: boolean;
  /** While true, the figure + its delta show placeholders (the endpoint is loading). */
  loading?: boolean;
  /** The backing API has no field for this figure — render a `?` instead of a number. */
  missing?: boolean;
  /** The backing API request is failing: render `N/A` and suppress the delta/caption. */
  error?: boolean;
  /** The data came but holds no figure to show (nothing to count): render a dash and suppress the delta. */
  indeterminate?: boolean;
};

/**
 * A label + counted-up value pair, with an optional inline delta pill and a
 * caption beneath. The acquisition split (registrations / FTDs) uses the delta +
 * caption; the financial / money-flow split stats use the bare `compact` figure.
 * The label/caption are static chrome, so only the figure + delta load in.
 */
export const Stat = ({
  label,
  value,
  prefix,
  suffix,
  delay,
  delta,
  caption,
  compact = false,
  loading = false,
  missing = false,
  error = false,
  indeterminate = false,
}: StatProps): React.ReactElement => (
  <Root>
    <Label text={label} tag="span" />
    <Value $compact={compact}>
      <StatValue
        value={value}
        prefix={prefix}
        suffix={suffix}
        delay={delay}
        loading={loading}
        missing={missing}
        error={error}
        indeterminate={indeterminate}
      />
      {/* On error, or with no figure, there is no real delta to show: drop the pill entirely. */}
      {delta !== undefined && !error && !indeterminate ? (
        loading ? (
          <Skeleton width={44} height={18} radius={8} />
        ) : (
          <Delta value={delta} />
        )
      ) : null}
    </Value>
    {caption && !error ? <Cap text={caption} tag="span" /> : null}
  </Root>
);

export default Stat;
